import { useMemo, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreateTrialVisitRequest,
  TRIAL_VISIT_CONTACTS,
  TRIAL_VISIT_KINDS,
  TRIAL_VISIT_TYPES,
  TrialVisitContactSchema,
  TrialVisitKindSchema,
  type TrialVisitContact,
  type TrialVisitDto,
  type TrialVisitGroupDto,
  type TrialVisitKind,
  type UpdateTrialVisitRequest,
} from '@ctms/contracts';
import { visitLabel } from '@ctms/shared';
import { isHTTPError } from 'ky';
import { AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import { createAppColumnHelper, useAppTable } from '../../../components/data-table/table-hook.js';
import { getApiErrorMessage } from '../../../api/client.js';
import { trialsApi } from '../trials.api.js';
import { trialQueryKeys } from '../trials.query-keys.js';
import { WindowModesDialog } from './window-modes.js';
import { BulkCreateVisitsDialog } from './bulk-create-visits-dialog.js';
import { BulkEditVisitsDialog } from './bulk-edit-visits-dialog.js';
import { VisitGroupsDialog, VisitGroupSwatch, groupColor } from './visit-groups-dialog.js';

export interface VisitCatalogProps {
  trialId: number;
  canWrite: boolean;
}

const KIND_OPTIONS: { value: TrialVisitKind; label: string }[] = [
  { value: TRIAL_VISIT_KINDS.SCREENING, label: 'Screening' },
  { value: TRIAL_VISIT_KINDS.RANDOMIZATION, label: 'Randomizacja' },
  { value: TRIAL_VISIT_KINDS.REGULAR, label: 'Rutynowa' },
  { value: TRIAL_VISIT_KINDS.END_OF_TREATMENT, label: 'Koniec leczenia' },
  { value: TRIAL_VISIT_KINDS.FOLLOW_UP, label: 'Follow-up' },
  { value: TRIAL_VISIT_KINDS.OUT_OF_PROTOCOL, label: 'Wycofanie' },
  { value: TRIAL_VISIT_KINDS.UNSCHEDULED, label: 'Nieplanowa' },
  { value: TRIAL_VISIT_KINDS.OTHER, label: 'Inna' },
];

const CONTACT_OPTIONS: { value: TrialVisitContact; label: string }[] = [
  { value: TRIAL_VISIT_CONTACTS.SITE, label: 'Ośrodek' },
  { value: TRIAL_VISIT_CONTACTS.PHONE, label: 'Telefon' },
  { value: TRIAL_VISIT_CONTACTS.HOME, label: 'Dom' },
  { value: TRIAL_VISIT_CONTACTS.SITE_IMP, label: 'Ośrodek – wyłącznie podanie leku' },
];

const fieldClass =
  'mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none disabled:opacity-50';

const labelClass = 'block text-xs font-medium uppercase tracking-wider text-slate-300';

const columnHelper = createAppColumnHelper<TrialVisitDto>();

/** Stable empty reference while the groups query is still loading. */
const NO_VISIT_GROUPS: TrialVisitGroupDto[] = [];

/** #44 E: sentinel marking the "bez grupy" pseudo-group chip (group ids are positive). */
const NO_GROUP_SENTINEL = -1;

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

function optionLabel(options: readonly { value: string; label: string }[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

/** Signed day offset as shown in the catalog. Zero stays unsigned; negatives use a minus sign. */
export function formatDayCount(value: number): string {
  if (value > 0) {
    return `+${value}`;
  }
  if (value < 0) {
    return `\u2212${Math.abs(value)}`;
  }
  return '0';
}

export function parseDayCount(value: string): number | undefined {
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    return undefined;
  }
  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed)) {
    return undefined;
  }
  return parsed;
}

/**
 * #44 pkt 6 (round 2): the contiguous run of selected visits containing
 * `anchorId`, in catalog order — moving a selected visit moves the whole run.
 */
export function contiguousSelectedBlock(
  visits: readonly TrialVisitDto[],
  selected: ReadonlySet<number>,
  anchorId: number,
): number[] {
  const anchor = visits.findIndex((visit) => visit.id === anchorId);
  if (anchor < 0) {
    return [anchorId];
  }
  let start = anchor;
  while (start > 0 && selected.has(visits[start - 1]!.id)) {
    start -= 1;
  }
  let end = anchor;
  while (end < visits.length - 1 && selected.has(visits[end + 1]!.id)) {
    end += 1;
  }
  return visits.slice(start, end + 1).map((visit) => visit.id);
}

export function formatVisitWindow(
  visit: Pick<TrialVisitDto, 'window' | 'minDays' | 'optDays' | 'maxDays' | 'referralVisitId'>,
  catalog: readonly Pick<TrialVisitDto, 'id' | 'visitName1' | 'visitName2'>[],
): string {
  if (!visit.window || visit.minDays == null || visit.optDays == null || visit.maxDays == null) {
    return 'bez okna';
  }
  const span = `min ${formatDayCount(visit.minDays)} / opt ${formatDayCount(visit.optDays)} / max ${formatDayCount(visit.maxDays)}`;
  const referral = catalog.find((row) => row.id === visit.referralVisitId);
  return referral == null ? span : `${span} od ${formatVisitName(referral)}`;
}

/**
 * Piotr (mail 5, uwaga 3): wizyta ma dwie niezależne nazwy. Wyświetlamy je jako
 * "Nazwa 1 (Nazwa 2)", a gdy drugiej nie ma — samo "Nazwa 1".
 */
export function formatVisitName(visit: { visitName1: string; visitName2: string | null }): string {
  return visitLabel(visit.visitName1, visit.visitName2);
}

/** #44 A5: symmetric ± day-spread presets offered while adding a single visit. */
export const VISIT_WINDOW_SPREAD_OPTIONS = [1, 2, 3, 5, 7, 14] as const;

/**
 * #44 A5: carries the previous visit's window shape over to a new MID. The
 * suggested bounds reuse the same day offsets around the centre (opt−min before,
 * max−opt after), so consecutive visits inherit the same window by default.
 * Returns null when there is no previous visit or it has no complete window.
 */
export function suggestWindowBoundsFromPrevious(
  optDays: number,
  previous: Pick<TrialVisitDto, 'minDays' | 'optDays' | 'maxDays'> | undefined,
): { minDays: number; maxDays: number } | null {
  if (previous == null || previous.minDays == null || previous.optDays == null || previous.maxDays == null) {
    return null;
  }
  const before = previous.optDays - previous.minDays;
  const after = previous.maxDays - previous.optDays;
  return { minDays: optDays - before, maxDays: optDays + after };
}

export const VisitFormSchema = z
  .object({
    visitName1: z.string().trim().min(1, 'Nazwa 1 jest wymagana').max(255),
    visitName2: z.string().trim().max(255),
    kind: TrialVisitKindSchema,
    contactType: TrialVisitContactSchema,
    description: z.string().max(10_000),
    window: z.boolean(),
    minDays: z.string(),
    optDays: z.string(),
    maxDays: z.string(),
    referralVisitId: z.string(),
    groupId: z.string(),
  })
  .superRefine((value, ctx) => {
    if (!value.window) {
      return;
    }
    // #44 atom 1 (A3/A4): only MAX is required — an empty minimum becomes 0
    // and an empty optimum is derived by the service as the range midpoint
    // (min + floor((max − min) / 2), #44 fix 01.11).
    if (parseDayCount(value.maxDays) === undefined) {
      ctx.addIssue({ code: 'custom', path: ['maxDays'], message: 'Maksimum jest wymagane' });
    }
    const referral = Number(value.referralVisitId);
    if (!Number.isInteger(referral) || referral <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['referralVisitId'],
        message: 'Wizyta referencyjna jest wymagana',
      });
    }
  });

export type VisitFormInput = z.infer<typeof VisitFormSchema>;

const EMPTY_VISIT_FORM: VisitFormInput = {
  visitName1: '',
  visitName2: '',
  kind: TRIAL_VISIT_KINDS.REGULAR,
  contactType: TRIAL_VISIT_CONTACTS.SITE,
  description: '',
  window: false,
  minDays: '',
  optDays: '',
  maxDays: '',
  referralVisitId: '',
  groupId: '',
};

/**
 * #44 W2: the group picker holds the sentinel `__new__` while a group is being
 * created inline. An empty value means "no group"; anything else must be a
 * positive integer id — the sentinel is blocked before submit.
 */
function parseGroupId(value: string): number | null {
  if (value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

export function toCreateTrialVisitRequest(trialId: number, data: VisitFormInput): CreateTrialVisitRequest {
  const description = data.description.trim();
  const request: CreateTrialVisitRequest = {
    trialId,
    visitName1: data.visitName1.trim(),
    kind: data.kind,
    // Legacy `type` (S/P/H/I) is no longer edited in the UI; the schema default keeps it stable.
    type: TRIAL_VISIT_TYPES.S,
    contactType: data.contactType,
    referenceType: 'visit',
    window: data.window,
  };
  if (description.length > 0) {
    request.description = description;
  }
  const visitName2 = data.visitName2.trim();
  if (visitName2.length > 0) {
    request.visitName2 = visitName2;
  }
  const groupId = parseGroupId(data.groupId);
  if (groupId !== null) {
    request.groupId = groupId;
  }
  if (!data.window) {
    return request;
  }
  const maxDays = parseDayCount(data.maxDays);
  const referralVisitId = Number(data.referralVisitId);
  if (maxDays === undefined || !Number.isInteger(referralVisitId) || referralVisitId <= 0) {
    throw new Error('Włączone okno wymaga maksimum i wizyty referencyjnej');
  }
  // #44 atom 1 (A3/A4): empty MIN/OPT are omitted — the service derives
  // minimum → 0 and optimum → the range midpoint min + floor((MAX−MIN)/2).
  const minDays = parseDayCount(data.minDays);
  if (minDays !== undefined) {
    request.minDays = minDays;
  }
  const optDays = parseDayCount(data.optDays);
  if (optDays !== undefined) {
    request.optDays = optDays;
  }
  return { ...request, maxDays, referralVisitId };
}

/**
 * D.4 (mail 6): the edit dialog sends the FULL form state as a patch, so an
 * emptied optional field (Nazwa 2, opis) is an explicit `null` that CLEARS
 * the stored value (unlike create, where an empty string simply omits the
 * field). Day bounds travel only with an enabled window — when the window is
 * off the stored bounds are left untouched.
 */
export function toUpdateTrialVisitRequest(data: VisitFormInput): UpdateTrialVisitRequest {
  const visitName2 = data.visitName2.trim();
  const description = data.description.trim();
  const request: UpdateTrialVisitRequest = {
    visitName1: data.visitName1.trim(),
    visitName2: visitName2.length > 0 ? visitName2 : null,
    kind: data.kind,
    contactType: data.contactType,
    window: data.window,
    description: description.length > 0 ? description : null,
    // An emptied picker is an explicit null — it detaches the visit from its group.
    groupId: parseGroupId(data.groupId),
  };
  if (!data.window) {
    return request;
  }
  const maxDays = parseDayCount(data.maxDays);
  const referralVisitId = Number(data.referralVisitId);
  if (maxDays === undefined || !Number.isInteger(referralVisitId) || referralVisitId <= 0) {
    throw new Error('Włączone okno wymaga maksimum i wizyty referencyjnej');
  }
  // #44 atom 1 (A3/A4): empty MIN/OPT travel as explicit nulls — the service
  // derives minimum → 0 and optimum → the range midpoint min + floor((MAX−MIN)/2).
  return {
    ...request,
    minDays: parseDayCount(data.minDays) ?? null,
    optDays: parseDayCount(data.optDays) ?? null,
    maxDays,
    referralVisitId,
  };
}

export function VisitCatalog({ trialId, canWrite }: VisitCatalogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState<TrialVisitDto | null>(null);
  const [windowModesVisit, setWindowModesVisit] = useState<TrialVisitDto | null>(null);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isGroupsOpen, setIsGroupsOpen] = useState(false);
  // #44 A-select: multi-visit selection lives in the catalog; bulk actions are a separate atom.
  const [selectedVisitIds, setSelectedVisitIds] = useState<ReadonlySet<number>>(() => new Set<number>());
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);

  const visitsQuery = useQuery({
    queryKey: trialQueryKeys.visits(trialId),
    queryFn: () => trialsApi.listVisits(trialId),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  // #44 A-colors: same query key as the group manager dialog, so both share one
  // cache entry (one fetch) — the palette index depends on this order.
  const groupsQuery = useQuery({
    queryKey: trialQueryKeys.visitGroups(trialId),
    queryFn: () => trialsApi.listVisitGroups(trialId),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const visits = visitsQuery.data ?? [];
  const groups = groupsQuery.data ?? NO_VISIT_GROUPS;

  // #44 A-select v1: a group chip selects exactly that group's visits and clears the rest;
  // a second click on the same chip clears the selection.
  const toggleGroup = (groupId: number): void => {
    if (selectedGroupId === groupId) {
      setSelectedVisitIds(new Set<number>());
      setSelectedGroupId(null);
      return;
    }
    setSelectedVisitIds(new Set(visits.filter((visit) => visit.groupId === groupId).map((visit) => visit.id)));
    setSelectedGroupId(groupId);
  };

  // #44 E: "bez grupy" acts as a pseudo-group chip selecting visits without a group.
  const toggleNoGroup = (): void => {
    if (selectedGroupId === NO_GROUP_SENTINEL) {
      setSelectedVisitIds(new Set<number>());
      setSelectedGroupId(null);
      return;
    }
    setSelectedVisitIds(new Set(visits.filter((visit) => visit.groupId === null).map((visit) => visit.id)));
    setSelectedGroupId(NO_GROUP_SENTINEL);
  };

  // #44 E: a single check toggles the whole catalog selection.
  const allSelected = visits.length > 0 && selectedVisitIds.size === visits.length;
  const toggleAll = (): void => {
    setSelectedGroupId(null);
    if (allSelected) {
      setSelectedVisitIds(new Set<number>());
    } else {
      setSelectedVisitIds(new Set(visits.map((visit) => visit.id)));
    }
  };

  // #44 pkt 6: trwała kolejność katalogu — swap numeru z sąsiadem, potem odświeżenie listy.
  const moveVisit = async (visit: TrialVisitDto, direction: 'up' | 'down'): Promise<void> => {
    try {
      // #44 pkt 6 (round 2): a selected visit moves the whole contiguous run.
      if (selectedVisitIds.has(visit.id)) {
        const block = contiguousSelectedBlock(visits, selectedVisitIds, visit.id);
        await trialsApi.reorderVisits(trialId, { ids: block, direction });
      } else {
        await trialsApi.reorderVisit(trialId, visit.id, { direction });
      }
      void queryClient.invalidateQueries({ queryKey: trialQueryKeys.visits(trialId) });
    } catch (error) {
      toast.error(await getApiErrorMessage(error, 'Nie udało się zmienić kolejności wizyt'));
    }
  };

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.display({
          id: 'select',
          header: '',
          cell: (info) => {
            const visit = info.row.original;
            return (
              <input
                type="checkbox"
                aria-label={`Zaznacz wizytę ${formatVisitName(visit)}`}
                checked={selectedVisitIds.has(visit.id)}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setSelectedVisitIds((current) => {
                    const next = new Set(current);
                    if (checked) {
                      next.add(visit.id);
                    } else {
                      next.delete(visit.id);
                    }
                    return next;
                  });
                }}
                className="h-4 w-4 rounded border border-slate-700 bg-slate-950 accent-cyan-500"
              />
            );
          },
        }),
        columnHelper.accessor('visitName1', {
          header: 'Nazwa',
          cell: (info) => {
            const visit = info.row.original;
            const color = groupColor(groups, visit.groupId);
            const groupName =
              visit.groupId === null
                ? null
                : (groups.find((group) => group.id === visit.groupId)?.name ?? null);
            return (
              <span className="flex items-center gap-2">
                {color !== null && groupName !== null && (
                  <VisitGroupSwatch color={color} name={groupName} />
                )}
                <span>{formatVisitName(visit)}</span>
              </span>
            );
          },
        }),
        columnHelper.accessor('kind', {
          header: 'Rodzaj',
          cell: (info) => optionLabel(KIND_OPTIONS, info.getValue()),
        }),
        columnHelper.accessor('contactType', {
          header: 'Kontakt',
          cell: (info) => optionLabel(CONTACT_OPTIONS, info.getValue()),
        }),
        columnHelper.display({
          id: 'window',
          header: 'Okno',
          cell: (info) => formatVisitWindow(info.row.original, visits),
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Działania',
          cell: (info) => {
            if (!canWrite) {
              return <span>—</span>;
            }
            const visit = info.row.original;
            const index = visits.findIndex((item) => item.id === visit.id);
            return (
              <>
                <button
                  type="button"
                  onClick={() => void moveVisit(visit, 'up')}
                  disabled={index === 0}
                  className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  W górę
                </button>
                <button
                  type="button"
                  onClick={() => void moveVisit(visit, 'down')}
                  disabled={index === visits.length - 1}
                  className="ml-1 rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  W dół
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingVisit(info.row.original);
                    setIsDialogOpen(true);
                  }}
                  className="ml-1 rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800"
                >
                  Edytuj
                </button>
                <button
                  type="button"
                  onClick={() => setWindowModesVisit(info.row.original)}
                  className="ml-1 rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800"
                >
                  Okno wizyty
                </button>
              </>
            );
          },
        }),
      ]),
    [visits, groups, selectedVisitIds],
  );
  const table = useAppTable({ data: visits, columns });

  const closeDialog = (): void => {
    setIsDialogOpen(false);
    setEditingVisit(null);
  };

  const handleSaved = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.visits(trialId) });
    closeDialog();
  };

  // #40: the bulk rows may carry a groupId — refresh the catalog AND the group list.
  const handleBulkCreated = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.visits(trialId) });
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.visitGroups(trialId) });
    setIsBulkOpen(false);
  };

  // #44 pkt 5: the stamped features may change the group-independent columns —
  // refresh the catalog and drop the selection (the action consumed it).
  const handleBulkEdited = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.visits(trialId) });
    setSelectedVisitIds(new Set<number>());
    setSelectedGroupId(null);
    setIsBulkEditOpen(false);
  };

  return (
    <section aria-label="Katalog wizyt protokołu" className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
      {visitsQuery.isLoading ? (
        <p className="py-6 text-sm text-slate-400">Ładowanie katalogu wizyt</p>
      ) : visitsQuery.isError ? (
        <div role="alert" className="text-sm text-rose-300">
          Nie udało się wczytać katalogu wizyt.
          <button type="button" onClick={() => void visitsQuery.refetch()} className="ml-3 underline">
            Spróbuj ponownie
          </button>
        </div>
      ) : visits.length === 0 ? (
        <div>
          <h3 className="text-sm font-semibold text-slate-100">Brak wizyt protokołu</h3>
          <p className="mt-2 text-xs text-slate-400">
            Dodaj co najmniej jedną wizytę z nazwą i typem kontaktu, zanim badanie posłuży do układania ścieżek pacjentów.
          </p>
          {canWrite && (
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setIsDialogOpen(true)}
                className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
              >
                Dodaj wizytę protokołu
              </button>
              <button
                type="button"
                onClick={() => setIsBulkOpen(true)}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
              >
                Dodaj wiele wizyt
              </button>
              <button
                type="button"
                onClick={() => setIsGroupsOpen(true)}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
              >
                Zarządzaj grupami
              </button>
            </div>
          )}
        </div>
      ) : (
        <div>
          <h3 className="text-sm font-semibold text-slate-100">Katalog wizyt protokołu</h3>
          <p className="mt-1 text-xs text-slate-400">Numer kolejny nadawany jest automatycznie przy dodaniu pozycji.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2" role="group" aria-label="Grupy wizyt">
            {groups.map((group) => {
              const color = groupColor(groups, group.id);
              return (
                <button
                  key={group.id}
                  type="button"
                  aria-pressed={selectedGroupId === group.id}
                  onClick={() => toggleGroup(group.id)}
                  className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
                    selectedGroupId === group.id
                      ? 'border-cyan-500 bg-cyan-500/10 text-cyan-100'
                      : 'border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800'
                  }`}
                >
                  {color !== null && <VisitGroupSwatch color={color} name={group.name} />}
                  <span>{group.name}</span>
                </button>
              );
            })}
            <button
              type="button"
              aria-pressed={selectedGroupId === NO_GROUP_SENTINEL}
              onClick={toggleNoGroup}
              className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
                selectedGroupId === NO_GROUP_SENTINEL
                  ? 'border-cyan-500 bg-cyan-500/10 text-cyan-100'
                  : 'border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800'
              }`}
            >
              <VisitGroupSwatch color="#64748b" name="Bez grupy" />
              <span>bez grupy</span>
            </button>
            <span className="text-xs text-slate-400">Zaznaczono: {selectedVisitIds.size}</span>
            <label className="flex items-center gap-1.5 text-xs text-slate-300">
              <input
                type="checkbox"
                aria-label="Zaznacz wszystkie wizyty"
                checked={allSelected}
                onChange={toggleAll}
                className="h-4 w-4 rounded border border-slate-700 bg-slate-950 accent-cyan-500"
              />
              Wszystkie
            </label>
            {canWrite && selectedVisitIds.size > 0 && (
              <button
                type="button"
                onClick={() => setIsBulkEditOpen(true)}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
              >
                Nadaj cechy zaznaczonym ({selectedVisitIds.size})
              </button>
            )}
          </div>
          <div className="mt-3 overflow-hidden rounded-lg border border-slate-800">
            <table className="w-full text-left text-xs">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="border-b border-slate-800 bg-slate-950/60">
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} scope="col" className="px-3 py-2 text-slate-400">
                        <table.FlexRender header={header} />
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-800/80">
                    {row.getAllCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2 text-slate-200">
                        <table.FlexRender cell={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canWrite && (
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setIsDialogOpen(true)}
                className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
              >
                Dodaj wizytę protokołu
              </button>
              <button
                type="button"
                onClick={() => setIsBulkOpen(true)}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
              >
                Dodaj wiele wizyt
              </button>
              <button
                type="button"
                onClick={() => setIsGroupsOpen(true)}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
              >
                Zarządzaj grupami
              </button>
            </div>
          )}
        </div>
      )}

      {isDialogOpen && (
        <VisitDialog
          trialId={trialId}
          visits={visits}
          visit={editingVisit ?? undefined}
          onClose={closeDialog}
          onSaved={handleSaved}
        />
      )}

      {windowModesVisit && (
        <WindowModesDialog
          trialId={trialId}
          visitId={windowModesVisit.id}
          visitName={windowModesVisit.visitName1}
          canWrite={canWrite}
          onClose={() => setWindowModesVisit(null)}
        />
      )}

      {isBulkOpen && (
        <BulkCreateVisitsDialog
          trialId={trialId}
          onCreated={handleBulkCreated}
          onClose={() => setIsBulkOpen(false)}
        />
      )}

      {isBulkEditOpen && selectedVisitIds.size > 0 && (
        <BulkEditVisitsDialog
          trialId={trialId}
          visits={visits}
          selectedIds={selectedVisitIds}
          onClose={() => setIsBulkEditOpen(false)}
          onSaved={handleBulkEdited}
        />
      )}

      {isGroupsOpen && (
        <VisitGroupsDialog
          trialId={trialId}
          visits={visits}
          onClose={() => setIsGroupsOpen(false)}
        />
      )}
    </section>
  );
}

function VisitDialog({
  trialId,
  visits,
  visit,
  onClose,
  onSaved,
}: {
  trialId: number;
  visits: TrialVisitDto[];
  visit?: TrialVisitDto;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<VisitFormInput>({
    resolver: zodResolver(VisitFormSchema),
    defaultValues: visit
      ? {
          visitName1: visit.visitName1,
          visitName2: visit.visitName2 ?? '',
          kind: visit.kind,
          contactType: visit.contactType,
          description: visit.description ?? '',
          window: visit.window,
          minDays: visit.minDays === null ? '' : String(visit.minDays),
          optDays: visit.optDays === null ? '' : String(visit.optDays),
          maxDays: visit.maxDays === null ? '' : String(visit.maxDays),
          referralVisitId: visit.referralVisitId === null ? '' : String(visit.referralVisitId),
          groupId: visit.groupId === null ? '' : String(visit.groupId),
        }
      : EMPTY_VISIT_FORM,
  });
  const windowEnabled = watch('window');
  const selectedGroupId = watch('groupId');

  // #44 W2: same group source as the bulk wizard (#44 W1).
  const groupsQuery = useQuery({
    queryKey: trialQueryKeys.visitGroups(trialId),
    queryFn: () => trialsApi.listVisitGroups(trialId),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });
  const groups = groupsQuery.data ?? [];
  // #44 D2: a referral must always be earlier than the visit it points from.
  // Add mode appends a new visit at the end, so every existing visit is earlier;
  // edit mode offers only the visits strictly before the edited row.
  const referralOptions = [...visits]
    .sort((left, right) => left.visitNumber - right.visitNumber || left.id - right.id)
    .filter((row) => {
      if (row.id === visit?.id) return false;
      if (visit === undefined) return true;
      return (
        row.visitNumber < visit.visitNumber ||
        (row.visitNumber === visit.visitNumber && row.id < visit.id)
      );
    });

  // #44 A5: while ADDING a visit, once the user fills MID we suggest MIN/MAX
  // from the previous visit's window shape (consecutive visits usually share it).
  const suggestFromPrevious = (): void => {
    if (visit) return;
    const optDaysValue = parseDayCount(getValues('optDays'));
    if (optDaysValue === undefined) return;
    // Never overwrite bounds the user already filled in.
    if (parseDayCount(getValues('minDays')) !== undefined || parseDayCount(getValues('maxDays')) !== undefined) {
      return;
    }
    const previous = visits[visits.length - 1];
    const suggestion = suggestWindowBoundsFromPrevious(optDaysValue, previous);
    if (suggestion === null) return;
    setValue('minDays', String(suggestion.minDays));
    setValue('maxDays', String(suggestion.maxDays));
  };

  // #44 A5: symmetric ± spread preset: MIN = MID − N, MAX = MID + N.
  const applySpread = (spread: number): void => {
    const optDaysValue = parseDayCount(getValues('optDays'));
    if (optDaysValue === undefined) {
      setErrorMessage('Podaj Optimum (MID), aby zastosować zakres ±.');
      return;
    }
    setValue('minDays', String(optDaysValue - spread));
    setValue('maxDays', String(optDaysValue + spread));
  };

  const onSubmit = async (data: VisitFormInput): Promise<void> => {
    setErrorMessage(null);
    if (data.groupId === '__new__') {
      setErrorMessage('Utwórz nową grupę albo wybierz inną opcję grupy.');
      return;
    }
    setIsSubmitting(true);
    try {
      if (visit) {
        await trialsApi.updateVisit(trialId, visit.id, toUpdateTrialVisitRequest(data));
      } else {
        await trialsApi.createVisit(trialId, toCreateTrialVisitRequest(trialId, data));
      }
      onSaved();
    } catch (err: unknown) {
      setErrorMessage(
        await getApiErrorMessage(err, visit ? 'Nie udało się zapisać zmian.' : 'Nie udało się zapisać wizyty protokołu.'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const dialogTitle = visit ? 'Edycja wizyty protokołu' : 'Dodaj wizytę protokołu';

  /** #44 W2: inline group creation — mirrored from the bulk wizard (#44 W1). */
  const handleCreateGroup = async (): Promise<void> => {
    const name = newGroupName.trim();
    if (name === '') {
      setErrorMessage('Nazwa nowej grupy nie może być pusta.');
      return;
    }
    setErrorMessage(null);
    setIsCreatingGroup(true);
    try {
      const created = await trialsApi.createVisitGroup(trialId, { name });
      queryClient.setQueryData<TrialVisitGroupDto[]>(
        trialQueryKeys.visitGroups(trialId),
        (current) => [...(current ?? []), created],
      );
      setValue('groupId', String(created.id));
      setNewGroupName('');
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się utworzyć grupy.'));
    } finally {
      setIsCreatingGroup(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={visit ? 'edit-visit-title' : 'add-visit-title'}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2 id={visit ? 'edit-visit-title' : 'add-visit-title'} className="text-base font-semibold text-slate-100">
              {dialogTitle}
            </h2>
            <p className="mt-1 text-xs text-slate-400">Nazwa i typ kontaktu są wymagane.</p>
            <p className="text-xs text-slate-400">Rodzaj wizyty służy późniejszemu filtrowaniu pacjentów.</p>
          </div>
          <button type="button" onClick={onClose} disabled={isSubmitting} aria-label="Zamknij" className="rounded-md p-1.5 text-slate-400">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4" noValidate>
          {errorMessage && (
            <div role="alert" className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <label htmlFor="visit-name" className={labelClass}>Nazwa 1</label>
            <input id="visit-name" type="text" disabled={isSubmitting} className={fieldClass} {...register('visitName1')} />
            {errors.visitName1 && <p className="mt-1 text-xs text-rose-400">{errors.visitName1.message}</p>}
          </div>

          <div>
            <label htmlFor="visit-name2" className={labelClass}>Nazwa 2 (opcjonalnie)</label>
            <input id="visit-name2" type="text" disabled={isSubmitting} className={fieldClass} {...register('visitName2')} />
            {errors.visitName2 && <p className="mt-1 text-xs text-rose-400">{errors.visitName2.message}</p>}
          </div>

          <div>
            <label htmlFor="visit-kind" className={labelClass}>Rodzaj</label>
            <select id="visit-kind" disabled={isSubmitting} className={fieldClass} {...register('kind')}>
              {KIND_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="visit-contact" className={labelClass}>Typ kontaktu</label>
            <select id="visit-contact" disabled={isSubmitting} className={fieldClass} {...register('contactType')}>
              {CONTACT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="visit-group" className={labelClass}>Grupa</label>
            {/* Controlled on purpose: the group options arrive asynchronously, and an uncontrolled
                select would keep the value it coerced on the first render (empty). */}
            <select
              id="visit-group"
              disabled={isSubmitting || isCreatingGroup}
              className={fieldClass}
              {...register('groupId')}
              value={selectedGroupId ?? ''}
              onChange={(event) => setValue('groupId', event.target.value)}
            >
              <option value="">Bez grupy</option>
              {groups.map((group) => (
                <option key={group.id} value={String(group.id)}>{group.name}</option>
              ))}
              <option value="__new__">+ Nowa grupa…</option>
            </select>
          </div>

          {selectedGroupId === '__new__' && (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label htmlFor="visit-new-group-name" className={labelClass}>Nazwa nowej grupy</label>
                <input
                  id="visit-new-group-name"
                  type="text"
                  disabled={isSubmitting || isCreatingGroup}
                  className={fieldClass}
                  value={newGroupName}
                  onChange={(event) => setNewGroupName(event.target.value)}
                />
              </div>
              <button
                type="button"
                onClick={() => void handleCreateGroup()}
                disabled={isSubmitting || isCreatingGroup}
                className="rounded-md bg-cyan-600 px-3.5 py-2 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
              >
                {isCreatingGroup ? 'Tworzenie…' : 'UTWÓRZ GRUPĘ'}
              </button>
            </div>
          )}

          <div>
            <label htmlFor="visit-description" className={labelClass}>Opis pomocniczy (opcjonalnie)</label>
            <input id="visit-description" type="text" disabled={isSubmitting} className={fieldClass} {...register('description')} />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input type="checkbox" disabled={isSubmitting} {...register('window')} />
            Włącz okno planowania
          </label>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label htmlFor="visit-min" className={labelClass}>Minimum (dni)</label>
              <input id="visit-min" type="text" disabled={isSubmitting || !windowEnabled} className={fieldClass} {...register('minDays')} />
              {errors.minDays && <p className="mt-1 text-xs text-rose-400">{errors.minDays.message}</p>}
            </div>
            <div>
              <label htmlFor="visit-opt" className={labelClass}>Optimum (dni)</label>
              <input id="visit-opt" type="text" disabled={isSubmitting || !windowEnabled} className={fieldClass} {...register('optDays', { onBlur: () => suggestFromPrevious() })} />
              {errors.optDays && <p className="mt-1 text-xs text-rose-400">{errors.optDays.message}</p>}
            </div>
            <div>
              <label htmlFor="visit-max" className={labelClass}>Maksimum (dni)</label>
              <input id="visit-max" type="text" disabled={isSubmitting || !windowEnabled} className={fieldClass} {...register('maxDays')} />
              {errors.maxDays && <p className="mt-1 text-xs text-rose-400">{errors.maxDays.message}</p>}
            </div>
          </div>

          {windowEnabled && (
            <p id="visit-window-auto-hint" className="text-xs text-slate-400">
              Puste pola zostaną wyliczone automatycznie: minimum → 0, optimum → środek
              zakresu (między minimum a maksimum, zaokrąglony w dół).
            </p>
          )}

          {!visit && windowEnabled && (
            <div>
              <span className={labelClass}>Szybki zakres ± (od Optimum)</span>
              <div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label="Wybierz zakres okna">
                {VISIT_WINDOW_SPREAD_OPTIONS.map((spread) => (
                  <button
                    key={spread}
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => applySpread(spread)}
                    className="rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-50"
                  >
                    ±{spread}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label htmlFor="visit-referral" className={labelClass}>Wizyta referencyjna</label>
            <select id="visit-referral" disabled={isSubmitting || !windowEnabled} className={fieldClass} {...register('referralVisitId')}>
              <option value="">—</option>
              {referralOptions.map((visit) => (
                <option key={visit.id} value={String(visit.id)}>
                  {formatVisitName(visit)}
                </option>
              ))}
            </select>
            {errors.referralVisitId && <p className="mt-1 text-xs text-rose-400">{errors.referralVisitId.message}</p>}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300">
              Anuluj
            </button>
            <button type="submit" disabled={isSubmitting} className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50">
              {isSubmitting ? 'Zapisywanie…' : visit ? 'Zapisz zmiany' : 'Zapisz wizytę'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
